import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-479436ad"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "booking-widget" }
const _hoisted_2 = { class: "booking-widget__service-selection" }
const _hoisted_3 = { class: "booking-widget__calendar-section" }
const _hoisted_4 = { class: "calendar-container" }
const _hoisted_5 = {
  key: 0,
  class: "calendar-loader"
}

import {
  computed,
  onMounted,
  onUnmounted,
  PropType,
  ref,
  watch,
  nextTick,
} from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { format, formatISO, startOfDay, endOfDay, isAfter } from 'date-fns'
import _ from 'lodash'

import {
  useEventsSlotsFetchMutation,
  useFetchDatesAvailability,
  useServicesFetchMutation,
  useServicesSlotsFetchMutation,
  useTicketsSlotsFetchMutation,
  usePublicCompanyLogoQuery,
} from '@/queries/public/bookingWidget'
import { useFetchTicketItemDateAvailabilityListMutation } from '@/queries/service/serviceTicket'
import { useFetchCompanyColorPreferencesWithToken } from '@/queries/settings.queries'

import WidgetCalendar from '@/components/booking-widget/WidgetCalendar.vue'
import BookingComponent from '@/components/booking-widget/BookingComponent.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'

import { ServiceType } from '@/enums/services'
import { CalendarType } from '@/models/google.model'
import { GroupedOption, StringDate } from '@/models/shared.model'
import { PublicService } from '@/models/public/bookingWidget.model'
import { Order } from '@/models/public/order.model'
import { COLORS } from '@/models/settings.model'
import {
  CompanyHousingDateAvailability,
  MinimumTimeslot,
} from '@/models/public/service.model'
import {
  ITicketItemDateAvailability,
  ITicketItemDateAvailabilityBody,
  ITicketItemDatePeriodAvailabilityBody,
} from '@/models/service/serviceTicket.model'

import {
  getMonthDaysForDate,
  getWeekdaysForDate,
  getWeekStartingFromDate,
} from '@/utils/overview-calendar'
import {
  filterOutSlots,
  findAllOutOfBoundsSelectedSlots,
  findAllSlotsBetweenSlotAndMinDuration,
  findAllSlotsBetweenSlotAndSelectedSlots,
  mapEventSlots,
  mapTicketSlots,
} from '@/utils/booking-widget'
import { formatServerDate } from '@/utils/formatters'


const __default__ = {
  name: 'BookingWidgetView',
}

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  service: {
    type: Object as PropType<PublicService | null>,
    default: null,
  },
  earliestStart: {
    type: Object as PropType<Date | null>,
    default: null,
  },
  start: {
    type: Object as PropType<Date | null>,
    default: null,
  },
  end: {
    type: Object as PropType<Date | null>,
    default: null,
  },
},
  emits: ['onStartChange', 'onEndChange', 'onOrderChange'],
  setup(__props, { emit: emits }) {

const props = __props





const i18n = useI18n()
const route = useRoute()

const selectedService = ref<PublicService | null>(null)
const groupedOptions = ref<GroupedOption[]>([])
const selectedStartDate = ref<Date | null>(null)
const selectedEndDate = ref<Date | null>(null)
const selectedAdultCount = ref<number>(1)
const selectedKidCount = ref<number>(0)
const slots = ref<MinimumTimeslot[]>([])
const selectedSlots = ref<MinimumTimeslot[]>([])
const housingAvailabilities = ref<CompanyHousingDateAvailability[]>([])
const ticketAvailabilities = ref<ITicketItemDateAvailability[]>([])
const days = ref(getWeekdaysForDate(new Date()))
const order = ref<Order | null>(null)
const companyToken = ref<string | null>(null)
const companyColorPreferences = ref()
const isLoading = ref(false)
const isUserInteraction = ref(false)
const isFetching = ref(false)

const { mutateAsync: mutateGetServices } = useServicesFetchMutation()
const { mutateAsync: mutateGetSlots } = useServicesSlotsFetchMutation()
const { mutateAsync: mutateGetEventSlots } = useEventsSlotsFetchMutation()
const { mutateAsync: mutateGetTicketSlots } = useTicketsSlotsFetchMutation()
const { mutateAsync: mutateGetHousingAvailability } =
  useFetchDatesAvailability()
const { mutateAsync: mutateGetTicketAvailabilities } =
  useFetchTicketItemDateAvailabilityListMutation()
const { mutateAsync: mutateGetCompanyColorPreferences } =
  useFetchCompanyColorPreferencesWithToken(companyToken)
const { data: companyLogo } = usePublicCompanyLogoQuery(companyToken)

const selectedCalendar = computed(() => {
  const serviceTypeMap = {
    [ServiceType.HOUSING]: CalendarType.MONTH,
    [ServiceType.EXPERIENCE]: CalendarType.WEEK,
    [ServiceType.EVENT]: CalendarType.WEEK,
    [ServiceType.TICKET]: CalendarType.DAY,
  }
  return selectedService.value?.type
    ? serviceTypeMap[selectedService.value.type]
    : null
})

const initializeFromProps = () => {
  if (props.service) {
    selectedService.value = props.service
    groupedOptions.value = groupOptions([props.service])
  }

  if (props.start) {
    selectedStartDate.value = props.start
  } else if (props.earliestStart) {
    selectedStartDate.value = props.earliestStart
  }

  if (props.end) {
    selectedEndDate.value = props.end
  }
}

const resetComponentState = () => {
  selectedService.value = null
  groupedOptions.value = []
  selectedStartDate.value = null
  selectedEndDate.value = null
  selectedAdultCount.value = 1
  selectedKidCount.value = 0
  slots.value = []
  selectedSlots.value = []
  housingAvailabilities.value = []
  ticketAvailabilities.value = []
  order.value = null
}

const groupOptions = (services: PublicService[]) => {
  const serviceTypeLabels = {
    [ServiceType.EVENT]: i18n.t('serviceType.eventLabel'),
    [ServiceType.EXPERIENCE]: i18n.t('serviceType.experienceLabel'),
    [ServiceType.HOUSING]: i18n.t('serviceType.housingLabel'),
    [ServiceType.TICKET]: i18n.t('serviceType.ticketLabel'),
  }

  const grouped = services.reduce((acc, service) => {
    const label = serviceTypeLabels[service.type]
    if (!label) return acc

    if (!acc[label]) {
      acc[label] = { key: label, items: [] }
    }
    acc[label].items.push(service)
    return acc
  }, {} as Record<string, GroupedOption>)

  return Object.values(grouped)
}

const updateDaysForService = () => {
  if (!selectedService.value) return

  const date = selectedStartDate.value || new Date()
  const dayUpdaters = {
    [ServiceType.HOUSING]: () => getMonthDaysForDate(date),
    [ServiceType.EXPERIENCE]: () => getWeekStartingFromDate(date),
    [ServiceType.EVENT]: () => getWeekStartingFromDate(date),
    [ServiceType.TICKET]: () => [startOfDay(date)],
  }

  const updater = dayUpdaters[selectedService.value.type]
  if (updater) {
    days.value = updater()
  }
}

const fetchDataForService = async () => {
  if (!selectedService.value || !days.value.length || isFetching.value) return

  isLoading.value = true

  try {
    const fetchMap = {
      [ServiceType.HOUSING]: fetchHousingAvailability,
      [ServiceType.EVENT]: fetchEventSlots,
      [ServiceType.EXPERIENCE]: fetchExperienceSlots,
      [ServiceType.TICKET]: async () => {
        await Promise.all([fetchTicketAvailabilities(), fetchTicketSlots()])
      },
    }

    const fetcher = fetchMap[selectedService.value.type]
    if (fetcher) {
      await fetcher()
    }
  } finally {
    isLoading.value = false
  }
}

const fetchHousingAvailability = async () => {
  if (!selectedService.value || !days.value.length || isFetching.value) return

  try {
    isFetching.value = true
    housingAvailabilities.value = await mutateGetHousingAvailability({
      company: selectedService.value.company,
      start: format(days.value[0], 'yyyy-MM-dd'),
      end: format(days.value[days.value.length - 1], 'yyyy-MM-dd'),
      services: [selectedService.value.hash_id],
      group_service: undefined,
    })
  } finally {
    isFetching.value = false
  }
}

const fetchTicketAvailabilities = async () => {
  if (!selectedService.value || !days.value.length) return

  try {
    const body:
      | ITicketItemDateAvailabilityBody
      | ITicketItemDatePeriodAvailabilityBody = {
      date: formatServerDate(days.value[0]) as StringDate,
    }
    ticketAvailabilities.value = await mutateGetTicketAvailabilities({
      hashId: selectedService.value.hash_id,
      body,
    })
  } catch (e) {
    console.error(e)
    ticketAvailabilities.value = []
  }
}

const fetchExperienceSlots = async () => {
  if (!selectedService.value || !days.value.length || isFetching.value) return

  try {
    isFetching.value = true
    slots.value = await mutateGetSlots({
      serviceId: selectedService.value.hash_id,
      body: {
        start: formatISO(startOfDay(days.value[0]), { representation: 'date' }),
        end: formatISO(endOfDay(days.value[days.value.length - 1]), {
          representation: 'date',
        }),
        group_size: selectedAdultCount.value,
      },
    })
  } finally {
    isFetching.value = false
  }
}

const fetchEventSlots = async () => {
  if (!selectedService.value || !days.value.length || isFetching.value) return

  try {
    isFetching.value = true
    const eventSlots = await mutateGetEventSlots({
      serviceId: selectedService.value.hash_id,
      body: {
        start: formatISO(startOfDay(days.value[0]), { representation: 'date' }),
        end: formatISO(endOfDay(days.value[days.value.length - 1]), {
          representation: 'date',
        }),
      },
    })
    slots.value = mapEventSlots(eventSlots)
  } finally {
    isFetching.value = false
  }
}

const fetchTicketSlots = async () => {
  if (!selectedService.value || !days.value.length || isFetching.value) return

  try {
    isFetching.value = true
    const ticketSlots = await mutateGetTicketSlots({
      serviceId: selectedService.value.hash_id,
      body: {
        start: formatISO(startOfDay(days.value[0]), { representation: 'date' }),
        end: formatISO(endOfDay(days.value[days.value.length - 1]), {
          representation: 'date',
        }),
      },
    })
    slots.value = mapTicketSlots(ticketSlots)
  } finally {
    isFetching.value = false
  }
}

const handleAutoSelectSlotsFromProps = async () => {
  if (isUserInteraction.value) return

  await nextTick()

  const nonHousingTypes = [
    ServiceType.EXPERIENCE,
    ServiceType.EVENT,
    ServiceType.TICKET,
  ]
  if (
    !nonHousingTypes.includes(selectedService.value?.type) ||
    !slots.value.length
  ) {
    return
  }

  const start = isAfter(props.earliestStart, props.start) ? null : props.start
  const firstSelectedSlot = start
    ? slots.value.find((slot) => +new Date(slot.start) === +start)
    : props.earliestStart
    ? slots.value.find((slot) =>
        isAfter(new Date(slot.start), props.earliestStart),
      )
    : undefined

  selectedSlots.value = []
  if (!firstSelectedSlot) return

  const handlers = getSlotHandlers()
  const handler = handlers[selectedService.value.type]
  if (!handler) return

  let newSlots =
    selectedService.value.type === ServiceType.TICKET
      ? handler(firstSelectedSlot.id as number, false)
      : handler(firstSelectedSlot, false)

  const lastSelectedSlot = props.end
    ? slots.value.find((slot) => +new Date(slot.end) === +props.end)
    : undefined

  if (
    lastSelectedSlot &&
    !_.isEqual(firstSelectedSlot, lastSelectedSlot) &&
    !isAfter(props.start, props.end) &&
    +props.start !== +props.end
  ) {
    const additionalSlots =
      selectedService.value.type === ServiceType.TICKET
        ? handler(lastSelectedSlot.id as number, false)
        : handler(lastSelectedSlot, false)

    newSlots = [...new Set([...newSlots, ...additionalSlots])]
  }

  selectedSlots.value = newSlots

  if (firstSelectedSlot) {
    selectedStartDate.value = new Date(firstSelectedSlot.start)
  }
}

const getSlotHandlers = () => ({
  [ServiceType.EVENT]: (slot: MinimumTimeslot) =>
    slots.value.filter((s) => _.isEqual(slot, s)),

  [ServiceType.EXPERIENCE]: (
    slot: MinimumTimeslot,
    shouldUnselectAlreadySelected = true,
  ) => {
    const isAlreadySelected = selectedSlots.value.some((s) =>
      _.isEqual(slot, s),
    )

    if (isAlreadySelected) {
      if (!shouldUnselectAlreadySelected) return selectedSlots.value
      const betweenSlots = findAllOutOfBoundsSelectedSlots(
        slot,
        selectedSlots.value,
      )
      return filterOutSlots(selectedSlots.value, [...betweenSlots, slot])
    }

    if (selectedSlots.value.length === 0) {
      return [slot, ...findAllSlotsBetweenSlotAndMinDuration(slot, slots.value)]
    }

    return [
      ...selectedSlots.value,
      ...findAllSlotsBetweenSlotAndSelectedSlots(
        slot,
        selectedSlots.value,
        slots.value,
      ),
      slot,
    ]
  },

  [ServiceType.TICKET]: (
    slotId: number,
    shouldUnselectAlreadySelected = true,
  ) => {
    const isAlreadySelected = selectedSlots.value.some((s) => s.id === slotId)
    if (isAlreadySelected && shouldUnselectAlreadySelected) {
      return selectedSlots.value.filter((s) => s.id !== slotId)
    }
    return [
      ...selectedSlots.value,
      ...slots.value.filter((s) => s.id === slotId),
    ]
  },
})

const handleSlotClick = (
  slot: MinimumTimeslot,
  shouldUnselectAlreadySelected?: boolean,
) => {
  if (isFetching.value) return

  isUserInteraction.value = true

  const handlers = getSlotHandlers()
  const handler = handlers[selectedService.value?.type]

  if (!handler) {
    isUserInteraction.value = false
    return
  }

  const newSlots =
    selectedService.value.type === ServiceType.TICKET
      ? handler(slot.id as number, shouldUnselectAlreadySelected)
      : handler(slot, shouldUnselectAlreadySelected)

  handleSelectNewSlots(newSlots, slot)

  setTimeout(() => {
    isUserInteraction.value = false
  }, 100)
}

const handleSelectNewSlots = (
  newSlots: MinimumTimeslot[],
  selectedSlot?: MinimumTimeslot,
) => {
  selectedSlots.value = newSlots

  if (
    selectedStartDate.value &&
    newSlots.length === 0 &&
    selectedService.value?.type !== ServiceType.TICKET
  ) {
    selectedStartDate.value = null
  } else if (selectedSlot) {
    selectedStartDate.value = new Date(selectedSlot.start)
  }
}

const debouncedAutoSelect = _.debounce(handleAutoSelectSlotsFromProps, 300)

const handleDaysChange = (newDays: Date[]) => {
  if (isUserInteraction.value) return
  days.value = newDays
}

const handleAddSelectedDate = (day: Date) => {
  isUserInteraction.value = true

  const currentStartDate = selectedStartDate.value
  const currentEndDate = selectedEndDate.value

  if ((currentStartDate && currentEndDate) || !currentStartDate) {
    selectedStartDate.value = day
    selectedEndDate.value = null
  } else {
    if (isAfter(day, currentStartDate)) {
      selectedStartDate.value = currentStartDate
      selectedEndDate.value = day
    } else {
      selectedStartDate.value = day
      selectedEndDate.value = currentStartDate
    }
  }

  setTimeout(() => {
    isUserInteraction.value = false
  }, 100)
}

const handleTicketItemSelect = (
  slotId: number | undefined,
  shouldUnselectAlreadySelected: boolean,
) => {
  if (isFetching.value) return

  isUserInteraction.value = true

  if (!slotId) {
    handleSelectNewSlots([])
    setTimeout(() => {
      isUserInteraction.value = false
    }, 100)
    return
  }

  const handlers = getSlotHandlers()
  const newSlots = handlers[ServiceType.TICKET](
    slotId,
    shouldUnselectAlreadySelected,
  )
  handleSelectNewSlots(newSlots)

  setTimeout(() => {
    isUserInteraction.value = false
  }, 100)
}

const emitDateChanges = () => {
  if (selectedService.value?.type === ServiceType.HOUSING) {
    const housingInfo = selectedService.value.housings_information

    if (props.earliestStart) {
      emits('onStartChange', new Date(props.earliestStart))
    }

    if (selectedEndDate.value && housingInfo?.check_out_until) {
      const [hours, minutes] = housingInfo.check_out_until
        .split(':')
        .map(Number)
      const checkOutDate = new Date(selectedEndDate.value)
      checkOutDate.setHours(hours, minutes, 0, 0)
      emits('onEndChange', checkOutDate)
    }
  } else {
    const firstSlot = selectedSlots.value[0]
    const lastSlot = selectedSlots.value[selectedSlots.value.length - 1]

    if (firstSlot) emits('onStartChange', new Date(firstSlot.start))
    if (lastSlot) emits('onEndChange', new Date(lastSlot.end))
  }
}

onMounted(async () => {
  companyToken.value = route.query.token as string

  if (companyToken.value) {
    try {
      const [services, colors] = await Promise.all([
        mutateGetServices(companyToken.value as string),
        mutateGetCompanyColorPreferences(),
      ])
      if (!props.service) {
        groupedOptions.value = groupOptions(services)
      }
      companyColorPreferences.value = colors || { ...COLORS }
    } catch (error) {
      console.error('Failed to initialize component:', error)
      companyColorPreferences.value = { ...COLORS }
    }
  } else {
    companyColorPreferences.value = { ...COLORS }
  }

  initializeFromProps()
})

onUnmounted(() => {
  debouncedAutoSelect.cancel()
})

watch(
  () => props.service,
  (newService, prevService) => {
    if (newService === null && prevService !== null) {
      resetComponentState()
      return
    }

    if (newService) {
      selectedService.value = newService
      groupedOptions.value = groupOptions([newService])
    }
  },
  { immediate: true },
)

watch(
  () => [props.start, props.end, props.earliestStart],
  (newValues, prevValues) => {
    if (isUserInteraction.value) return
    if (_.isEqual(newValues, prevValues)) return

    initializeFromProps()
  },
  { deep: true },
)

watch(selectedService, async (newService, prevService) => {
  if (!newService) return

  if (prevService && newService.id === prevService.id) return

  updateDaysForService()
  selectedSlots.value = []
  selectedAdultCount.value = newService.min_group_size

  if (newService.type === ServiceType.TICKET && !selectedStartDate.value) {
    selectedStartDate.value = new Date()
  }

  await fetchDataForService()
  if (!isUserInteraction.value) {
    debouncedAutoSelect()
  }
})

watch(
  () => [selectedService.value, days.value],
  async (newValues, prevValues) => {
    if (isUserInteraction.value) return
    if (_.isEqual(newValues, prevValues)) return
    if (!selectedService.value || !days.value.length) return

    await fetchDataForService()
    if (!isUserInteraction.value) {
      debouncedAutoSelect()
    }
  },
  { deep: true },
)

watch(
  () => [selectedService.value, selectedAdultCount.value],
  async (newValues, prevValues) => {
    if (isUserInteraction.value) return
    if (_.isEqual(newValues, prevValues)) return
    if (
      !selectedService.value ||
      selectedService.value.type !== ServiceType.EXPERIENCE
    )
      return

    await fetchExperienceSlots()
    if (!isUserInteraction.value) {
      debouncedAutoSelect()
    }
  },
)

watch(
  () => [selectedStartDate.value, selectedEndDate.value, selectedSlots.value],
  () => {
    nextTick(() => {
      emitDateChanges()
    })
  },
  { deep: true },
)

watch(order, (newOrder) => {
  if (newOrder) {
    emits('onOrderChange', newOrder)
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(BookingComponent, {
        "selected-start-date": selectedStartDate.value,
        "onUpdate:selected-start-date": _cache[0] || (_cache[0] = ($event: any) => ((selectedStartDate).value = $event)),
        "selected-end-date": selectedEndDate.value,
        "onUpdate:selected-end-date": _cache[1] || (_cache[1] = ($event: any) => ((selectedEndDate).value = $event)),
        "selected-service": selectedService.value,
        "onUpdate:selected-service": _cache[2] || (_cache[2] = ($event: any) => ((selectedService).value = $event)),
        "selected-adult-count": selectedAdultCount.value,
        "onUpdate:selected-adult-count": _cache[3] || (_cache[3] = ($event: any) => ((selectedAdultCount).value = $event)),
        "selected-kid-count": selectedKidCount.value,
        "onUpdate:selected-kid-count": _cache[4] || (_cache[4] = ($event: any) => ((selectedKidCount).value = $event)),
        order: order.value,
        "onUpdate:order": _cache[5] || (_cache[5] = ($event: any) => ((order).value = $event)),
        "grouped-options": groupedOptions.value,
        "selected-slots": selectedSlots.value,
        "housing-availabilities": housingAvailabilities.value,
        "ticket-availabilities": ticketAvailabilities.value,
        onOnTimeslotSelect: handleTicketItemSelect
      }, null, 8, ["selected-start-date", "selected-end-date", "selected-service", "selected-adult-count", "selected-kid-count", "order", "grouped-options", "selected-slots", "housing-availabilities", "ticket-availabilities"])
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        (isLoading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createVNode(LoadingSpinner)
            ]))
          : (_openBlock(), _createBlock(WidgetCalendar, {
              key: 1,
              "selected-start-date": selectedStartDate.value,
              "onUpdate:selected-start-date": _cache[6] || (_cache[6] = ($event: any) => ((selectedStartDate).value = $event)),
              "selected-end-date": selectedEndDate.value,
              "onUpdate:selected-end-date": _cache[7] || (_cache[7] = ($event: any) => ((selectedEndDate).value = $event)),
              service: selectedService.value,
              "selected-slots": selectedSlots.value,
              slots: slots.value,
              days: days.value,
              "selected-calendar": _unref(selectedCalendar),
              "housing-availabilities": housingAvailabilities.value,
              "ticket-availabilities": ticketAvailabilities.value,
              "company-logo": _unref(companyLogo),
              onOnSlotClick: handleSlotClick,
              onOnDaysChange: handleDaysChange,
              onOnAddSelectedDate: handleAddSelectedDate
            }, null, 8, ["selected-start-date", "selected-end-date", "service", "selected-slots", "slots", "days", "selected-calendar", "housing-availabilities", "ticket-availabilities", "company-logo"]))
      ])
    ])
  ]))
}
}

})